import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../AxiosInstance';
import { Toast, Modal, DetailRow, PageHeader, SearchBar, Pagination, DeleteConfirmModal, BtnSecondary, ActionButtons, LoadingSpinner, EmptyState } from './v2-utils';
import '../styles/v2.css';

const COLORS = ['linear-gradient(135deg,#f87171,#ef4444)','linear-gradient(135deg,#fbbf24,#d97706)','linear-gradient(135deg,#60a5fa,#3b82f6)','linear-gradient(135deg,#a78bfa,#8b5cf6)','linear-gradient(135deg,#34d399,#10b981)','linear-gradient(135deg,#f472b6,#ec4899)','linear-gradient(135deg,#fb923c,#ea580c)'];
const PAGE_SIZES = [6, 12, 18];

function getInitials(name) {
  if (!name) return '??';
  return name.split(/[\s\-()\[\]]+/).filter(Boolean).map(w=>w[0]).join('').toUpperCase().slice(0,2);
}
function formatProductName(p) {
  let s = p.product_name||'';
  if (p.variety) s+=` - ${p.variety}`;
  if (p.brand)   s+=` (${p.brand})`;
  if (p.size)    s+=` [${p.size}]`;
  return s;
}
function formatStock(v) {
  if (v===null||v===undefined) return '—';
  const n=Number(v); if(Number.isNaN(n)) return '—';
  return Number.isInteger(n)?String(n):n.toFixed(2);
}

export default function ViewInventoryV2() {
  const navigate = useNavigate();
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [page, setPage]           = useState(0);
  const [pageSize, setPageSize]   = useState(6);
  const [selected, setSelected]   = useState(null);
  const [toDelete, setToDelete]   = useState(null);
  const [toast, setToast]         = useState(null);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 900);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    axiosInstance.get('/inventories').then(r => setInventory(r.data||[])).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  useEffect(() => { setPage(0); }, [search]);

  const filtered = inventory.filter(item => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return `${item.product_name} ${item.brand||''} ${item.variety||''} ${item.size||''}`.toLowerCase().includes(q);
  });
  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated  = filtered.slice(page*pageSize, page*pageSize+pageSize);

  const stockStatus = (item) => {
    const stock = Number(item.current_stock||0);
    const limit = Number(item.stock_limit||0);
    if (stock <= 0) return { label:'Out', bg:'#fef2f2', color:'#b91c1c', dot:'#ef4444', pulse:true };
    if (limit > 0 && stock <= limit) return { label:'Low', bg:'#fffbeb', color:'#92400e', dot:'#fbbf24', pulse:true };
    return { label:'OK', bg:'#f0fdf4', color:'#15803d', dot:'#10b981', pulse:false };
  };

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/inventories/${toDelete.inventory_id}`);
      setInventory(prev => prev.filter(i => i.inventory_id !== toDelete.inventory_id));
      setToast({ message: 'Inventory record deleted.', severity: 'success' });
    } catch { setToast({ message: 'Failed to delete.', severity: 'error' }); }
    finally { setToDelete(null); }
  };

  if (loading) return <LoadingSpinner message="Loading inventory…" />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader title="Inventory" subtitle={`${filtered.length} item${filtered.length!==1?'s':''}`} />

      <SearchBar value={search} onChange={setSearch} placeholder="Search by product name, brand, variety…" />

      {paginated.length === 0 ? <EmptyState message={search ? 'No inventory matches.' : 'No inventory records yet.'} /> : isDesktop ? (
        <div className="v2-card" style={{ overflow: 'hidden' }}>
          <table className="v2-table" style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ borderBottom:'1px solid #f1f5f1' }}>
                {['Product','Current Stock','Unit','Threshold','Status','Actions'].map(h=>(
                  <th key={h} style={{ padding:'12px 20px', textAlign:h==='Product'?'left':'center', fontSize:10.5, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.07em', whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginated.map((item, i) => {
                const status = stockStatus(item);
                return (
                  <tr key={item.inventory_id} style={{ borderBottom:i<paginated.length-1?'1px solid #f8faf8':'none' }}>
                    <td style={{ padding:'13px 20px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:11 }}>
                        <div style={{ width:32, height:32, borderRadius:9, background:COLORS[i%COLORS.length], display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                          <span style={{ color:'white', fontSize:11, fontWeight:900 }}>{getInitials(item.product_name)}</span>
                        </div>
                        <p style={{ margin:0, fontSize:13, fontWeight:600, color:'#1e293b', maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{formatProductName(item)}</p>
                      </div>
                    </td>
                    <td style={{ padding:'13px 20px', textAlign:'center', fontSize:13, fontWeight:700, color:'#334155' }}>{formatStock(item.current_stock)}</td>
                    <td style={{ padding:'13px 20px', textAlign:'center', fontSize:13, color:'#64748b' }}>{item.unit_type||'—'}</td>
                    <td style={{ padding:'13px 20px', textAlign:'center', fontSize:13, color:'#64748b' }}>{item.stock_limit!=null?Math.ceil(item.stock_limit):'—'}</td>
                    <td style={{ padding:'13px 20px', textAlign:'center' }}>
                      <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 8px', borderRadius:99, fontSize:11, fontWeight:600, background:status.bg, color:status.color }}>
                        <span className={status.pulse?'v2-pulse':''} style={{ width:6, height:6, borderRadius:'50%', background:status.dot, display:'inline-block' }} />
                        {status.label}
                      </span>
                    </td>
                    <td style={{ padding:'13px 20px' }}>
                      <ActionButtons
                        onEdit={() => navigate(`/new/dashboard/inventories/reconcile/${item.inventory_id}`)}
                        onInfo={() => setSelected(item)}
                        onDelete={() => setToDelete(item)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {paginated.map((item, i) => {
            const status = stockStatus(item);
            return (
              <div key={item.inventory_id} className="v2-card" style={{ padding:18, overflow:'hidden' }}>
                <div style={{ display:'flex', alignItems:'flex-start', gap:12, marginBottom:14 }}>
                  <div style={{ width:40, height:40, borderRadius:12, background:COLORS[i%COLORS.length], display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <span style={{ color:'white', fontSize:12, fontWeight:900 }}>{getInitials(item.product_name)}</span>
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <p style={{ margin:0, fontSize:14, fontWeight:700, color:'#1e293b', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{formatProductName(item)}</p>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 8px', borderRadius:99, fontSize:11, fontWeight:600, background:status.bg, color:status.color, marginTop:4 }}>
                      <span className={status.pulse?'v2-pulse':''} style={{ width:6, height:6, borderRadius:'50%', background:status.dot, display:'inline-block' }} />
                      {status.label}
                    </span>
                  </div>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, paddingTop:14, borderTop:'1px solid #f1f5f1', marginBottom:14 }}>
                  {[{label:'Stock',value:formatStock(item.current_stock)},{label:'Unit',value:item.unit_type||'—'},{label:'Threshold',value:item.stock_limit!=null?Math.ceil(item.stock_limit):'—'}].map(it=>(
                    <div key={it.label} style={{ textAlign:'center' }}>
                      <p style={{ margin:0, fontSize:10, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:3 }}>{it.label}</p>
                      <p style={{ margin:0, fontSize:14, fontWeight:700, color:'#334155' }}>{it.value}</p>
                    </div>
                  ))}
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={() => navigate(`/new/dashboard/inventories/reconcile/${item.inventory_id}`)} style={{ flex:1, padding:'8px 0', borderRadius:10, border:'1.5px solid #a7f3d0', background:'#f0fdf4', color:'#065f46', fontSize:12, fontWeight:600, cursor:'pointer' }}>Reconcile</button>
                  <button onClick={() => setSelected(item)} style={{ flex:1, padding:'8px 0', borderRadius:10, border:'1.5px solid #e2e8f0', background:'white', color:'#475569', fontSize:12, fontWeight:600, cursor:'pointer' }}>Details</button>
                  <button onClick={() => setToDelete(item)} style={{ flex:1, padding:'8px 0', borderRadius:10, border:'1.5px solid #fecaca', background:'#fef2f2', color:'#ef4444', fontSize:12, fontWeight:600, cursor:'pointer' }}>Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Pagination page={page} totalPages={totalPages} pageSize={pageSize} totalItems={filtered.length}
        onPageChange={setPage} onPageSizeChange={p => { setPageSize(p); setPage(0); }} pageSizes={PAGE_SIZES} />

      <Modal open={!!selected} onClose={() => setSelected(null)} title="Inventory Details"
        footer={<BtnSecondary onClick={() => setSelected(null)}>Close</BtnSecondary>}>
        {selected && <>
          <DetailRow label="Product" value={formatProductName(selected)} />
          <DetailRow label="Current Stock" value={formatStock(selected.current_stock)} />
          <DetailRow label="Unit" value={selected.unit_type} />
          <DetailRow label="Low Stock Threshold" value={selected.stock_limit!=null?Math.ceil(selected.stock_limit).toString():'Not set'} />
          <DetailRow label="Target Stock" value={selected.target_stock!=null?selected.target_stock:'Not set'} />
        </>}
      </Modal>

      <DeleteConfirmModal open={!!toDelete} onClose={() => setToDelete(null)} onConfirm={handleDelete}
        itemDescription={toDelete ? formatProductName(toDelete) : ''} />

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
